import { useCallback, useEffect, useRef, useState } from "react";
import type { AttendeeAgendaState, AttendeeRemovedAgendaItem, AttendeeSession } from "@verity-connect/contracts";
import { queueAgendaChanges, readClientId, readPersonalAgenda, writePersonalAgenda, type PersonalAgendaStorage } from "./personal";
import { attendeeRunIsCurrent, attendeeStateClearsPrivateState, emitAttendeeStateChanged, useAttendeeStateEpoch } from "./cross-device-state";

export function usePersonalAgenda(session: AttendeeSession | null, eventSlug: string, validItemIds: readonly string[], online: boolean) {
  const signal = useAttendeeStateEpoch();
  const agendaPath = `/api/attendee/events/${encodeURIComponent(eventSlug)}/agenda`;
  const validIds = new Set(validItemIds);
  const initial = readPersonalAgenda();
  const [agenda, setAgenda] = useState<PersonalAgendaStorage>({ ...initial, itemIds: initial.itemIds.filter((id) => validIds.has(id)) });
  const [syncStatus, setSyncStatus] = useState<"device" | "syncing" | "synced" | "offline">(online ? "device" : "offline");
  /*
   * What she saved that is no longer on the schedule.
   *
   * Server state, held apart from the stored agenda on purpose. The line below
   * filters her stored items down to what the guide still carries, which is what
   * made a removed session vanish without a word in the first place; keeping this
   * outside that storage means the answer cannot be filtered away by the same rule.
   */
  const [removed, setRemoved] = useState<readonly AttendeeRemovedAgendaItem[]>([]);
  const activeRun = useRef(0);
  const nextRun = useRef(0);
  const scope = `${session?.identityId ?? "signed-out"}:${eventSlug}`;
  const scopeRef = useRef(scope);
  if (scopeRef.current !== scope) activeRun.current = 0;
  scopeRef.current = scope;

  const persist = useCallback((next: PersonalAgendaStorage) => { setAgenda(next); writePersonalAgenda(next); }, []);
  const clearAgenda = useCallback(() => {
    activeRun.current = 0;
    persist({ itemIds: [], outbox: [], identityId: null, revision: 0, synchronizedAt: null });
    setSyncStatus(online ? "device" : "offline");
  }, [online, persist]);
  const updateItemIds = useCallback((itemIds: readonly string[]) => {
    const current = readPersonalAgenda();
    persist(queueAgendaChanges(current, itemIds.filter((id) => validIds.has(id))));
  }, [persist, validItemIds.join("|")]);

  const synchronize = useCallback(async () => {
    if (!session || !online || activeRun.current !== 0) { if (!online) setSyncStatus("offline"); return; }
    const run = ++nextRun.current;
    const runScope = `${session.identityId}:${eventSlug}`;
    const captured = { scope: runScope, generation: run };
    activeRun.current = run;
    const current = () => attendeeRunIsCurrent(captured, { scope: scopeRef.current, generation: activeRun.current });
    setSyncStatus("syncing");
    try {
      const response = await fetch(agendaPath, { credentials: "same-origin" });
      if (!response.ok) throw new Error("The personal schedule could not be loaded.");
      const server = await response.json() as AttendeeAgendaState;
      if (!current()) return;
      setRemoved(server.removed ?? []);
      let local = readPersonalAgenda();
      if (local.identityId && local.identityId !== session.identityId) local = { itemIds: server.itemIds, outbox: [], identityId: session.identityId, revision: server.revision, synchronizedAt: server.synchronizedAt };
      else if (!local.identityId && local.outbox.length === 0 && local.itemIds.length > 0) local = queueAgendaChanges({ itemIds: [], outbox: [], identityId: null, revision: 0, synchronizedAt: null }, local.itemIds);
      if (local.outbox.length > 0) {
        const syncResponse = await fetch(`${agendaPath}/sync`, { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify({ clientId: readClientId(), mutations: local.outbox }) });
        if (!syncResponse.ok) throw new Error("The personal schedule could not be synchronized.");
        const next = await syncResponse.json() as AttendeeAgendaState;
        if (!current()) return;
        setRemoved(next.removed ?? []);
        const applied = new Set(next.appliedMutationIds);
        local = { itemIds: next.itemIds, outbox: local.outbox.filter((mutation) => !applied.has(mutation.id)), identityId: session.identityId, revision: next.revision, synchronizedAt: next.synchronizedAt };
        emitAttendeeStateChanged("write");
      } else local = { itemIds: server.itemIds, outbox: [], identityId: session.identityId, revision: server.revision, synchronizedAt: server.synchronizedAt };
      if (!current()) return;
      persist({ ...local, itemIds: local.itemIds.filter((id) => validIds.has(id)) });
      setSyncStatus("synced");
    } catch { if (current()) setSyncStatus(online ? "device" : "offline"); } finally { if (activeRun.current === run) activeRun.current = 0; }
  }, [agendaPath, eventSlug, online, persist, session?.identityId, validItemIds.join("|")]);

  useEffect(() => { void synchronize(); }, [synchronize, agenda.outbox.length, signal.epoch]);
  useEffect(() => {
    if (attendeeStateClearsPrivateState(signal.reason)) clearAgenda();
  }, [clearAgenda, signal.epoch, signal.reason]);
  useEffect(() => {
    const changed = (event: Event) => {
      const reason = (event as CustomEvent<string>).detail;
      if (reason === "signed-out" || reason === "account-deleted") clearAgenda();
    };
    window.addEventListener("wvcea:attendee-signed-out", clearAgenda);
    window.addEventListener("wvcea:attendee-state-changed", changed);
    return () => {
      window.removeEventListener("wvcea:attendee-signed-out", clearAgenda);
      window.removeEventListener("wvcea:attendee-state-changed", changed);
    };
  }, [clearAgenda]);

  return { itemIds: agenda.itemIds, removed, updateItemIds, syncStatus };
}
